import React, { useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { rrss } from "../data/links";
import cardsData from "../data/cards.json";

function getDescriptionClass(desc) {
  if (!desc) return "card-description-text";
  if (desc.length > 500) return "card-description-text xxxsmall";
  if (desc.length > 400) return "card-description-text xxsmall";
  if (desc.length > 300) return "card-description-text xsmall";
  if (desc.length > 200) return "card-description-text small";
  return "card-description-text";
}

function CardItem({ card, starsState, setStarsState }) {
  const selectedStars = starsState[card.id] || 0;
  const styleClass = selectedStars > 0 ? card[`class_${selectedStars}`] : "";
  const visibleLevel = selectedStars > 0 ? selectedStars : 1;
  const descText = card[`desc_${visibleLevel}`] || card.desc;

  const handleStarClick = (index) => {
    const newStars = index + 1 === selectedStars ? index : index + 1;
    setStarsState((prev) => ({
      ...prev,
      [card.id]: newStars
    }));
  };

  return (
    <li className={`card ${styleClass}`} data-packs={card.packs.join(",")} data-type={card.type}>
      <div className="card-bg">
        <div className="card-title separator-down">
          <p>{card.name}</p>
        </div>
        <div className="card-description">
          <div className="card-description-bg">
            <p className="card-description-title">{card[`descTitle_${visibleLevel}`] || card.descTitle}</p>
            <p className={getDescriptionClass(descText)}>{descText}</p>
          </div>
        </div>
        <div className="card-footer separator-up">
        {Array.from({ length: card.stars }, (_, i) => i + 1).map((lvl) => (
            card[`hp_${lvl}`] && (
              <React.Fragment key={lvl}>
                <p className={`card-footer-item hp separator-right ${visibleLevel === lvl ? "" : "hide"}`}>
                {card[`hp_${lvl}`]
                  .toString()
                  .split("\n")
                  .map((line, index) => {
                    const cleanLine = line.trim();
                    const displayLine = /hp/i.test(cleanLine) ? cleanLine : `${cleanLine} HP`;
                    return (
                      <React.Fragment key={index}>
                        {displayLine}
                        <br />
                      </React.Fragment>
                    );
                  })}
                </p>
                <p className={`card-footer-item atk separator-right ${visibleLevel === lvl ? "" : "hide"}`}>
                {card[`atk_${lvl}`]
                  .toString()
                  .split("\n")
                  .map((line, index) => {
                    const cleanLine = line.trim();
                    const displayLine = /atk/i.test(cleanLine) ? cleanLine : `${cleanLine} ATK`;
                    return (
                      <React.Fragment key={index}>
                        {displayLine}
                        <br />
                      </React.Fragment>
                    );
                  })}
                </p>
              </React.Fragment>
            )
          ))}
          <div className="card-footer-item stars">
            {Array.from({ length: card.stars }, (_, i) => (
              <input
                key={i}
                type="checkbox"
                className={`star-${i + 1}`}
                data-style={card[`class_${i + 1}`]}
                checked={i < selectedStars}
                onChange={() => handleStarClick(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </li>
  );
}

function CategoryCard({ title, data, starsState, setStarsState }) {
  return data.length ? (
    <div className="category-wrapper">
      <p className="category-title">{title}</p>
      <ul className="category-gallery">
        {data.map((card) => (
          <CardItem
            key={card.id}
            card={card}
            starsState={starsState}
            setStarsState={setStarsState}
          />
        ))}
      </ul>
    </div>
  ) : null;
}

export default function Tcg() {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedPacks, setSelectedPacks] = useState([]);
  const [starsState, setStarsState] = useState({});
  const [copyMessage, setCopyMessage] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const starsParam = params.get("stars");
    if (starsParam) {
      const newStars = {};
      starsParam.split(",").forEach((pair) => {
        const [id, stars] = pair.split(":");
        newStars[id] = Number(stars);
      });
      setStarsState(newStars);
    }
  }, []);

  const visibleCards = cardsData.filter(card => card.hide !== 1);

  const sortedCards = [...visibleCards].sort((a, b) =>
    a.sort !== b.sort ? Number(a.sort) - Number(b.sort) : a.name.localeCompare(b.name)
  );

  const types = [...new Set(visibleCards.map(card => card.type))];
  const packs = [...new Set(visibleCards.flatMap(card => card.packs))];

  const filteredCards = sortedCards.filter(card => {
    const matchType = selectedTypes.length === 0 || selectedTypes.includes(card.type);
    const matchPack = selectedPacks.length === 0 || card.packs.some(pack => selectedPacks.includes(pack));
    return matchType && matchPack;
  });

  const handleGenerateUrl = () => {
    const starsParam = Object.entries(starsState)
      .filter(([_, stars]) => stars > 0)
      .map(([id, stars]) => `${id}:${stars}`)
      .join(",");
  
    const url = `${window.location.origin}${window.location.pathname}?stars=${encodeURIComponent(starsParam)}`;
    navigator.clipboard.writeText(url);
    
    setCopyMessage("URL copiada al portapapeles");
    setTimeout(() => setCopyMessage(""), 3000);
  };
  
  return (
  <>
   <HelmetProvider>
      <Helmet>
        <title>TCG | ERI</title>
      </Helmet>
    </HelmetProvider>
    <div id="tcg" className="content">
      <div className="content-wrapper">
        <div className="content-title">
          <h1 className="title">Trading Card Game</h1>
          <h2 className="subtitle">
            En nuestro <a className="social-url dc" title="Discord" href={rrss.dc} target="_blank" rel="noopener noreferrer">servidor de Discord</a> tenemos cartas coleccionables que se consiguen jugando y participando.
            <span className="break">Aquí puedes visualizar y guardar tu progreso de la colección.</span>
          </h2>
        </div>
        <div className="content-top">
          <div className="top-items">
            <button className="btn btn-secondary mobile-filters-btn" onClick={() => setShowFilters(!showFilters)}>
                {showFilters ? "Cerrar filtros" : "Mostrar filtros"}
              </button>

            <button className="btn btn-primary" onClick={handleGenerateUrl}>Generar URL</button>
            {copyMessage && (
                <div className="copy-message fade-in">{copyMessage}</div>
              )}
          </div>
        </div>
        <div className="content-main">
          <div className={`content-left ${showFilters ? "visible" : ""}`}>
            <div className="filter separator-down">
              <p className="filter-title">Rareza</p>
              {types.map(type => (
                <div key={type} className="filter-item">
                  <input
                    type="checkbox"
                    id={`type-${type.toLowerCase}`}
                    checked={selectedTypes.includes(type)}
                    onChange={() => {
                      setSelectedTypes(prev =>
                        prev.includes(type)
                          ? prev.filter(t => t !== type)
                          : [...prev, type]
                      );
                    }}
                  />
                  <label htmlFor={`type-${type}`}>{type}</label>
                </div>
              ))}
            </div>
            <div className="filter">
              <p className="filter-title">Packs</p>
              {packs.map(pack => (
                <div key={pack} className="filter-item">
                  <input
                    type="checkbox"
                    id={`pack-${pack}`}
                    checked={selectedPacks.includes(pack)}
                    onChange={() => {
                      setSelectedPacks(prev =>
                        prev.includes(pack)
                          ? prev.filter(p => p !== pack)
                          : [...prev, pack]
                      );
                    }}
                  />
                  <label htmlFor={`pack-${pack}`}>{pack}</label>
                </div>
              ))}
            </div>
          </div>
          <div className="content-center">
            {types.map(type => (
                <CategoryCard
                key={type}
                title={type}
                data={filteredCards.filter(card => card.type === type)}
                starsState={starsState}
                setStarsState={setStarsState}
              />
              ))}
          </div>
        </div>
      </div>
    </div>
  </>
  );
}
